<template>
  <div
    id="dev-PaymentMethodSelector"
    class="payment-method-selector"
  >
    <div
      v-if="loading"
      class="loading-overlay"
    >
      <div
        class="spinner-border text-energy-orange"
        role="status"
      >
        <span class="visually-hidden">Processando...</span>
      </div>
    </div>

    <div
      v-if="disableDelete"
      class="alert alert-info py-2 px-3 mb-3 small"
    >
      <font-awesome-icon
        icon="circle-info"
        class="me-2"
      />
      {{ $t('manage_payment.disable_delete_rental_in_progress') }}
    </div>

    <div class="selector-list">
      <!-- PIX Option -->
      <div
        v-if="hasPix"
        class="selection-item"
        :class="{
          active: isDefaultPix,
          'to-remove': markingToRemove === 'pix',
          disabled: disableChange
        }"
        @click="selectExisting('pix')"
      >
        <div class="item-info">
          <span class="item-title">PIX</span>
          <span class="item-subtitle">{{ user.pix_identification.name }}</span>
        </div>

        <div
          class="item-actions"
          @click.stop
        >
          <template v-if="disableDelete">
            <!-- During active rental: no action buttons, only display -->
            <div class="selection-indicator"></div>
          </template>

          <template v-else-if="markingToRemove === 'pix'">
            <BaseButton
              bare
              variant=""
              class="btn-confirm-delete"
              @click="confirmRemove('pix')"
            >
              <font-awesome-icon icon="check" />
            </BaseButton>
            <BaseButton
              bare
              variant=""
              class="btn-cancel-delete"
              @click="markingToRemove = null"
            >
              <font-awesome-icon icon="xmark" />
            </BaseButton>
          </template>

          <template v-else>
            <BaseButton
              bare
              variant=""
              class="btn-delete"
              title="Excluir"
              @click="markToRemove('pix')"
            >
              <font-awesome-icon icon="trash" />
            </BaseButton>
            <div
              class="selection-indicator"
              @click="selectExisting('pix')"
            ></div>
          </template>
        </div>
      </div>

      <!-- Credit Card Options -->
      <div
        v-for="card in allCards"
        :key="card.id"
        class="selection-item"
        :class="{
          active: isDefaultCard(card.id),
          'to-remove': markingToRemove === card.id,
          disabled: disableChange
        }"
        @click="selectExisting('card', card)"
      >
        <div class="item-info">
          <span class="item-title">Cartão de Crédito</span>
          <span class="item-subtitle">•••• •••• •••• {{ card.last_digits || card.number?.slice(-4) }} ({{ card.flag || 'Crédito' }})</span>
        </div>

        <div
          class="item-actions"
          @click.stop
        >
          <template v-if="disableDelete">
            <!-- During active rental: no action buttons, only display -->
            <div class="selection-indicator"></div>
          </template>

          <template v-else-if="markingToRemove === card.id">
            <BaseButton
              bare
              variant=""
              class="btn-confirm-delete"
              @click="confirmRemove('card', card.id)"
            >
              <font-awesome-icon icon="check" />
            </BaseButton>
            <BaseButton
              bare
              variant=""
              class="btn-cancel-delete"
              @click="markingToRemove = null"
            >
              <font-awesome-icon icon="xmark" />
            </BaseButton>
          </template>

          <template v-else>
            <BaseButton
              bare
              variant=""
              class="btn-delete"
              title="Excluir"
              @click="markToRemove(card.id)"
            >
              <font-awesome-icon icon="trash" />
            </BaseButton>
            <div
              class="selection-indicator"
              @click="selectExisting('card', card)"
            ></div>
          </template>
        </div>
      </div>

      <!-- Add Actions -->
      <div
        v-if="!showCardForm && !showPixForm"
        class="list-actions mt-4"
      >
        <BaseButton
          bare
          variant=""
          class="action-link"
          :class="{ disabled: disableChange }"
          @click="!disableChange && (showCardForm = true)"
        >
          + Adicionar novo cartão
        </BaseButton>

        <BaseButton
          bare
          variant=""
          class="action-link"
          :class="{ disabled: disableChange }"
          @click="!disableChange && (showPixForm = true)"
        >
          + {{ hasPix ? 'Alterar dados do PIX' : 'Configurar PIX' }}
        </BaseButton>
      </div>

      <!-- Registration Forms -->
      <div
        v-if="showCardForm"
        class="form-container"
      >
        <div class="form-header-simple">
          <span>Novo Cartão</span>
          <BaseButton
            bare
            variant=""
            class="btn-back"
            @click="showCardForm = false"
          >
            Voltar
          </BaseButton>
        </div>
        <CreditCardRegistrationForm
          :user="user"
          @payment-added="handlePaymentAdded"
          @cancel="showCardForm = false"
        />
      </div>

      <div
        v-if="showPixForm"
        class="form-container"
      >
        <div class="form-header-simple">
          <span>Configurar PIX</span>
          <BaseButton
            bare
            variant=""
            class="btn-back"
            @click="showPixForm = false"
          >
            Voltar
          </BaseButton>
        </div>
        <PixRegistrationForm
          :user="user"
          @payment-added="handlePaymentAdded"
          @cancel="showPixForm = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import PixRegistrationForm from './PixRegistrationForm.vue'
  import CreditCardRegistrationForm from './CreditCardRegistrationForm.vue'
  import defaultCard from '@/views/home/service/defaultCard'
  import deleteCard from '@/views/home/service/deleteCard'
  import deletePixIdentification from '@/views/home/service/deletePixIdentification'
  import handleError from '@/services/handleError'
  import BaseButton from '@/components/ui/BaseButton.vue'

  const PIX_DEFAULT_ID = 0

  export default {
    name: 'PaymentMethodSelector',
    components: {
      PixRegistrationForm,
      CreditCardRegistrationForm,
      BaseButton
    },
    props: {
      user: {
        type: Object,
        required: true
      },
      initialMethod: {
        type: String,
        default: null
      },
      disableDelete: {
        type: Boolean,
        default: false
      },
      disableChange: {
        type: Boolean,
        default: false
      }
    },
    emits: ['payment-method-added', 'cancel', 'user-updated'],
    data() {
      return {
        showPixForm: false,
        showCardForm: false,
        loading: false,
        markingToRemove: null,
        PIX_DEFAULT_ID
      }
    },
    computed: {
      hasPix() {
        return !!(
          this.user?.pix_identification?.name &&
          this.user?.pix_identification?.cpf_or_cnpj
        )
      },
      isDefaultPix() {
        // In e2go API, if default_card is null but pix exists, it might be the default
        // but usually the user object tells us.
        return this.hasPix && !this.user.default_card
      },
      isDefaultCard() {
        return cardId => this.user.default_card && this.user.default_card.id === cardId
      },
      allCards() {
        if (!this.user || !this.user.credit_cards) return []
        return this.user.credit_cards
      }
    },
    methods: {
      async handlePaymentAdded(paymentData) {
        if (paymentData && paymentData.user) {
          this.$emit('user-updated', paymentData.user)
        }
        this.showCardForm = false
        this.showPixForm = false

        // If we just added a new method, it usually becomes default via API
        this.$emit('payment-method-added', paymentData)
      },

      async selectExisting(type, card = null) {
        if (this.loading || this.disableChange) return

        this.loading = true
        try {
          const targetId = type === 'pix' ? PIX_DEFAULT_ID : card.id

          // Call API to set this as default
          const updatedUser = await defaultCard(this.user, targetId)

          if (updatedUser) {
            this.$emit('user-updated', updatedUser)

            const paymentData = {
              type: type,
              user: updatedUser
            }

            this.$emit('payment-method-added', paymentData)
          }
        } catch (error) {
          handleError(error)
        } finally {
          this.loading = false
        }
      },

      markToRemove(id) {
        this.markingToRemove = id
      },

      async confirmRemove(type, cardId = null) {
        this.loading = true
        try {
          let updatedUser = null
          if (type === 'pix') {
            updatedUser = await deletePixIdentification(this.user)
          } else {
            updatedUser = await deleteCard(this.user, cardId)
          }

          if (updatedUser) {
            this.$emit('user-updated', updatedUser)
            this.markingToRemove = null
          }
        } catch (error) {
          handleError(error)
        } finally {
          this.loading = false
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  $color-energy-orange: #f58a48;
  $color-energy-blue: #016ab3;

  .payment-method-selector {
    width: 100%;
    position: relative;
  }

  .loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.7);
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
  }

  .selector-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .selection-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.25rem 1rem;
    background: #fcfcfc;
    border: 1.5px solid #eee;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(.to-remove) {
      background: #f5f5f5;
      border-color: #ddd;
    }

    &.active {
      border-color: $color-energy-orange;
      background: rgba($color-energy-orange, 0.03);

      .selection-indicator {
        border-color: $color-energy-orange;
        background: $color-energy-orange;

        &::after {
          content: '';
          width: 6px;
          height: 6px;
          background: white;
          border-radius: 50%;
        }
      }
    }

    &.to-remove {
      border-color: #ffcdd2;
      background: #fff9f9;
      cursor: default;
    }

    &.disabled {
      cursor: not-allowed;
      opacity: 0.8;
    }
  }

  .item-info {
    display: flex;
    flex-direction: column;
    text-align: left;
    flex: 1;
  }

  .item-title {
    font-size: 0.95rem;
    font-weight: 700;
    color: #333;
  }

  .item-subtitle {
    font-size: 0.8rem;
    color: #777;
    margin-top: 0.15rem;
  }

  .item-actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  .btn-delete {
    background: transparent;
    border: none;
    color: #ccc;
    padding: 0.5rem;
    font-size: var(--fs-sm);
    letter-spacing: -0.01em;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      color: #e74c3c;
    }
  }

  .btn-confirm-delete {
    background: #e74c3c;
    border: none;
    color: white;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(231, 76, 60, 0.3);
  }

  .btn-cancel-delete {
    background: #f0f0f0;
    border: none;
    color: #666;
    width: 2.2rem;
    height: 2.2rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .selection-indicator {
    width: 20px;
    height: 20px;
    border: 2px solid #ddd;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }

  .list-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: center;
    padding-top: 0.5rem;
  }

  .action-link {
    background: none;
    border: none;
    color: $color-energy-blue;
    font-size: 0.875rem;
    font-weight: 700;
    cursor: pointer;
    padding: 0.5rem;

    &:hover {
      text-decoration: underline;
    }
  }

  .form-container {
    margin-top: 0.5rem;
    padding: 1.5rem;
    border: 1px solid #eee;
    border-radius: 16px;
    background: white;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
    animation: slideDown 0.3s ease-out;
  }

  .form-header-simple {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f0f0f0;

    span {
      font-weight: 800;
      font-size: 1rem;
      color: #2c3e50;
    }

    .btn-back {
      background: #f8f9fa;
      border: 1px solid #e0e0e0;
      color: #666;
      font-size: 0.75rem;
      font-weight: 700;
      padding: 0.4rem 0.8rem;
      border-radius: 6px;
      cursor: pointer;

      &:hover {
        background: #eee;
        color: #333;
      }
    }
  }

  .text-energy-orange {
    color: $color-energy-orange !important;
  }

  /* Ensure alert-danger messages inside child components are centered and readable
   Use deep selector so scoped styles apply to nested components like registration forms */
  ::v-deep .alert-danger {
    text-align: center;
    font-size: 1.05rem;
    font-weight: 700;
    padding: 0.75rem 1rem;
    margin: 0.6rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 576px) {
    .selection-item {
      padding: 1rem;
    }

    ::v-deep .alert-danger {
      font-size: 1rem;
    }
  }
</style>
